import {createSlice, PayloadAction, isAnyOf} from '@reduxjs/toolkit';

import {
    UserIdentity,
    PageSubscriptionType,
    StubPage,
    ToolboxType,
    PageErrors,
    AccessRequestInfo,
    PageType,
} from '~shared/models/wiki';

import {DocTypeContent} from '~shared/models';

import {isString} from '~ui/utils';

import {
    createPage,
    importPage,
    createPageWithDetails,
    importPageWithDetails,
    getPageByIdWithDetails,
} from '~ui/store/thunks/pages';
import {finishPagesMove} from '~ui/store/actions';

import {addComment, deleteComment, getComments} from '../comments';
import {actions as commentsActions} from '../comments/slice';
import {getRevisions, actions as revisionActions} from '../revisions';
import {actions as attachmentsActions} from '../attachments/slice';
import {getAttachments, deleteAttachment, attachFiles} from '../attachments/thunks';
import {getSubscribers, addSubscribers, deleteSubscriber} from '../subscribers';
import {createFavorite, deleteFavorite} from '../panels/favorites';
import {
    loadNavigationTreeNode,
    getMoreNavigationTreeItems,
    loadNavigationTree,
} from '../navigation-tree/thunks';
import {createSubscription, deleteSubscriptions} from '../panels/subscriptions';
import {deletePage, requestAuthorRole} from '../../thunks';

import {
    getPageAcl,
    updatePageBackground,
    updatePageRedirect,
    updatePageContent,
    translatePage,
    updatePageActuality,
    updatePageType,
    updatePageSettings,
} from './thunks';

import {
    setPageDetails,
    setPageAttributes,
    setPageAuthors,
    setPageComments,
    decrementPageCommentsCount,
    incrementPageCommentsCount,
    setPageBookmark,
    updatePagesSlugs,
    setPageSubscription,
    setPageSubscribers,
    addPageSubscribers,
    deletePageSubscriber,
    getSlugByIdentifier,
    setPageContent,
    setStubPageDetails,
    SetPageDetailsData as SetDetailsPayload,
    SetPageAttributesData as SetAttributesPayload,
    SetPageAuthorsData as SetAuthorsPayload,
    SetContentData as SetContentPayload,
    setPageAcl,
    setPageAclLoading,
    setPageAuthorsLoading,
    setPageAuthorsError,
    setPageBackground,
    setNavigationTreePageDetails,
    setPageRedirectLoading,
    setPageRedirect,
    getDeletedPageTitle,
    setPageRevisions,
    SetRevisionsData as SetRevisionsDataPayload,
    setPageRevisionsData,
    setPageAttachments,
    deletePageAttachment,
    attachFilesToPage,
    setPageAttachmentsLoading,
    setPageAttachmentsError,
    setPageActuality,
    setPageToc,
    SetPageTocData as SetTocPayload,
    getPageRedirectUrl,
    SetPageAclData as SetPageAclPayload,
    setPagePermissions,
    SetPagePermissionsData as SetPagePermissionsPayload,
} from './utils';
import {GetDetailsPayload, GetRevisionPayload, PagesState} from './types';

const initialState: PagesState = {
    current: '',
    preservedTitle: '',
    isLoading: false,
    isUpdating: false,
    entities: {},
    stubEntities: {},
    slugByPath: {},
    byId: {},
    toolboxType: 'empty',
};
interface DeleteContentPayload {
    path: string | null;
}

interface GetStubDetailsPayload {
    slug: string;
    error: PageErrors;
}

interface UpdateTitlePayload {
    id: number;
    title: string;
}

interface UpdateAuthorsPayload {
    id: number;
    authors?: UserIdentity[];
    owner?: UserIdentity;
    preventSelflock?: 0 | 1;
    forceUpdateDetails?: boolean;
}
interface SetLoadingPayload {
    isLoading: boolean;
}

interface SetErrorPayload {
    error: PageErrors;
}
interface SetRedirectToPayload {
    url: string;
}

interface ResetSubscribersPayload {
    pageId: number;
}
interface SetTitlePayload {
    id: number;
    title: string;
}

type SetStubPageDetailsPayload = StubPage;

interface SetPathPayload {
    slug: string;
    path: string;
}

interface SetToolboxTypePayload {
    type: ToolboxType;
}

interface SetStubPendingAccessRequestPayload {
    slug: string;
    pendingAccessRequest: AccessRequestInfo;
}
interface SetPreviewYfmPayload {
    id: number;
    previewYfm?: boolean;
}

interface GetPermissionsPayload {
    id: string;
}

const {reducer, actions} = createSlice({
    name: 'pages',
    initialState,
    reducers: {
        setRevisionsData(state, {payload}: PayloadAction<SetRevisionsDataPayload>) {
            setPageRevisionsData(state, payload);
        },

        getDetails(state, {payload}: PayloadAction<GetDetailsPayload>) {
            state.isLoading = true;
            state.redirectTo = undefined;

            const entity = state.entities[payload.slug];

            if (!entity) {
                return;
            }

            entity.html = undefined;
            entity.translatedHtml = undefined;
        },
        getErrorPageDetails(state, {payload}: PayloadAction<GetStubDetailsPayload>) {
            state.shouldGetErrorPageDetails = undefined;

            state.isLoading = true;
            setPageAclLoading(state, {slug: payload.slug, isLoading: true});
            setPageAuthorsLoading(state, {slug: payload.slug, isLoading: true});
        },
        setDetails(state, {payload}: PayloadAction<SetDetailsPayload>) {
            state.error = undefined;
            state.redirectTo = undefined;

            setPageDetails(state, payload);

            const stubEntity = state.stubEntities[payload.slug];

            if (stubEntity) {
                delete state.stubEntities[payload.slug];
            }
        },
        setToc(state, {payload}: PayloadAction<SetTocPayload>) {
            state.error = undefined;
            state.redirectTo = undefined;

            setPageToc(state, payload);
        },
        setPath(state, {payload}: PayloadAction<SetPathPayload>) {
            state.slugByPath[payload.path] = payload.slug;
        },
        setCurrentDetails(state, {payload}: PayloadAction<SetDetailsPayload>) {
            state.current = payload.slug;
            state.error = undefined;
            state.isLoading = false;
            state.toolboxType = payload.toolboxType || 'normal';
            state.redirectTo = undefined;

            setPageDetails(state, payload);

            const stubEntity = state.stubEntities[payload.slug];

            if (stubEntity) {
                delete state.stubEntities[payload.slug];
            }
        },
        setCurrentStubDetails(state, {payload}: PayloadAction<SetStubPageDetailsPayload>) {
            state.current = payload.slug;
            state.isLoading = false;

            setStubPageDetails(state, payload);

            const entity = state.entities[payload.slug];

            if (entity) {
                delete state.entities[payload.slug];
            }
        },
        setPageAcl(state, {payload}: PayloadAction<SetPageAclPayload>) {
            setPageAclLoading(state, {slug: payload.slug, isLoading: false});

            setPageAcl(state, payload);
        },

        setToolboxType(state, {payload}: PayloadAction<SetToolboxTypePayload>) {
            state.toolboxType = payload.type;
        },
        setAuthors(state, {payload}: PayloadAction<SetAuthorsPayload>) {
            const {slug} = payload;
            setPageAuthorsLoading(state, {slug, isLoading: false});
            setPageAuthors(state, payload);
        },
        setPermissions(state, {payload}: PayloadAction<SetPagePermissionsPayload>) {
            setPagePermissions(state, payload);
        },
        setContent(state, {payload}: PayloadAction<SetContentPayload>) {
            setPageContent(state, payload);
        },
        setAttributes(state, {payload}: PayloadAction<SetAttributesPayload>) {
            setPageAttributes(state, payload);
        },
        getMissingDetails(state, {payload}: PayloadAction<GetDetailsPayload>) {
            const {slug} = payload;
            setPageAuthorsLoading(state, {slug, isLoading: true});
        },
        getRevisionDetails(state, {payload}: PayloadAction<GetRevisionPayload>) {
            state.isLoading = true;
            state.redirectTo = undefined;

            const slug = state.slugByPath[payload.pagePath];

            if (!slug) {
                return;
            }

            const entity = state.entities[slug];

            if (!entity) {
                return;
            }

            entity.html = undefined;
            entity.translatedHtml = undefined;
        },
        updateTitle(state, {payload}: PayloadAction<UpdateTitlePayload>) {
            const slug = state.byId[payload.id];
            const entity = slug ? state.entities[slug] : undefined;

            if (!entity) return;

            state.preservedTitle = entity.title;

            entity.title = payload.title;
            entity.translatedTitle = undefined;
        },
        updateAuthors(state, {payload}: PayloadAction<UpdateAuthorsPayload>) {
            const {id} = payload;
            const slug = state.byId[id];

            if (!slug) return;

            setPageAuthorsLoading(state, {slug, isLoading: true});
            setPageAuthorsError(state, {slug, error: ''});
        },
        deleteContent(state, {payload: {path}}: PayloadAction<DeleteContentPayload>) {
            state.redirectTo = undefined;

            const slug = path ? state.slugByPath[path] : undefined;

            if (!slug) {
                return;
            }

            const entity = slug ? state.entities[slug] : undefined;

            if (!entity) {
                return;
            }

            entity.html = undefined;
            entity.content = undefined;
            entity.toc = undefined;
            entity.translatedTitle = undefined;
            entity.revisionsData = undefined;
            entity.revisions = [];
        },
        setLoading(state, {payload: {isLoading}}: PayloadAction<SetLoadingPayload>) {
            state.isLoading = isLoading;
        },
        setTitle(state, {payload}: PayloadAction<SetTitlePayload>) {
            const slug = state.byId[payload.id];
            const entity = slug ? state.entities[slug] : undefined;

            if (!entity) return;

            entity.title = payload.title;
        },
        setPreviewYfm(state, {payload}: PayloadAction<SetPreviewYfmPayload>) {
            const {id, previewYfm} = payload;
            const slug = state.byId[id];
            const entity = slug ? state.entities[slug] : undefined;

            if (!entity) {
                return;
            }

            if (Boolean(entity.previewYfm) !== previewYfm) {
                entity.html = undefined;
                entity.translatedHtml = undefined;
                entity.previewYfm = previewYfm;
            }
        },
        setError(state, {payload: {error}}: PayloadAction<SetErrorPayload>) {
            state.redirectTo = undefined;
            state.error = error;
        },
        setRedirectTo(state, {payload}: PayloadAction<SetRedirectToPayload>) {
            state.redirectTo = payload.url;
        },
        setAuthorsLoadingById(state, {payload}: PayloadAction<{isLoading: boolean; id: number}>) {
            const {id, isLoading} = payload;
            const slug = state.byId[id];

            if (!slug) return;

            setPageAuthorsLoading(state, {slug, isLoading});
        },
        setAuthorsError(state, {payload}: PayloadAction<{error: string; id: number}>) {
            const {id, error} = payload;
            const slug = state.byId[id];

            if (!slug) return;

            setPageAuthorsError(state, {slug, error});
        },
        resetSubscribers(state, {payload}: PayloadAction<ResetSubscribersPayload>) {
            const slug = state.byId[payload.pageId];
            const entity = slug ? state.entities[slug] : undefined;

            if (!entity) return;

            entity.subscribers = undefined;
        },
        setStubPendingAccessRequest(
            state,
            {payload}: PayloadAction<SetStubPendingAccessRequestPayload>,
        ) {
            const {slug, pendingAccessRequest} = payload;
            const entity = state.stubEntities[slug];

            if (!entity) return;

            entity.pendingAccessRequest = pendingAccessRequest;
        },
        getPermissions(_state, _action: PayloadAction<GetPermissionsPayload>) {},
    },
    extraReducers: (builder) =>
        builder
            .addCase(getComments.fulfilled, (state, {payload: {comments, pageId}}) => {
                setPageComments(state, {
                    pageId,
                    comments: comments.results,
                    prevCursor: comments.prev_cursor,
                    nextCursor: comments.next_cursor,
                });
            })
            .addCase(addComment.fulfilled, (state, {payload: {comment, pageId}}) => {
                const slug = state.byId[pageId];
                const entity = slug ? state.entities[slug] : undefined;

                if (!entity) return;

                if (!comment.thread_id) {
                    const index = (entity.comments || []).indexOf(comment.id);
                    if (index === -1) {
                        entity.comments?.push(comment.id);
                    }
                }

                incrementPageCommentsCount(state, entity.slug);
            })
            .addCase(deleteComment.fulfilled, (state, {payload: {comment, pageId}}) => {
                const slug = state.byId[pageId];
                const entity = slug ? state.entities[slug] : undefined;

                if (!entity) return;

                const index = (entity.comments || []).indexOf(comment.id);

                if (index !== -1) {
                    entity.comments?.splice(index, 1);
                }

                decrementPageCommentsCount(state, entity.slug);
            })
            .addCase(createFavorite.fulfilled, (state, {payload}) => {
                const {
                    page: {slug},
                    id,
                } = payload;

                setPageBookmark(state, {slug, bookmarkId: id});
            })
            .addCase(deleteFavorite.fulfilled, (state, action) => {
                const {slug} = action.meta.arg;

                setPageBookmark(state, {slug});
            })
            .addCase(loadNavigationTree.fulfilled, (state, {payload}) => {
                const {children, breadcrumbs_branch: breadcrumbsBranch} = payload;
                const {results} = children;

                breadcrumbsBranch?.forEach((child) => {
                    setNavigationTreePageDetails(state, child);
                });
                results.forEach((child) => {
                    setNavigationTreePageDetails(state, child);
                });
            })
            .addCase(loadNavigationTreeNode.fulfilled, (state, {payload}) => {
                const {children} = payload;
                const {results} = children;

                results.forEach((child) => {
                    setNavigationTreePageDetails(state, child);
                });
            })
            .addCase(getMoreNavigationTreeItems.fulfilled, (state, {payload}) => {
                const {results} = payload;

                results.forEach((child) => {
                    setNavigationTreePageDetails(state, child);
                });
            })
            .addCase(getSubscribers.fulfilled, (state, action) => {
                const {pageId} = action.meta.arg;
                const {payload} = action;

                setPageSubscribers(state, {
                    id: pageId,
                    results: payload.results,
                    hasNext: payload.has_next,
                    pageId: payload.page_id,
                });
            })
            .addCase(addSubscribers.fulfilled, (state, action) => {
                const {payload} = action;
                const {pageId, isCluster, isCurrentUser} = action.meta.arg;

                addPageSubscribers(state, {
                    id: pageId,
                    results: payload.results,
                });

                const slug = state.byId[pageId];

                if (slug && isCurrentUser) {
                    setPageSubscription(state, {
                        slug,
                        subscription: isCluster
                            ? PageSubscriptionType.ToCluster
                            : PageSubscriptionType.ToPage,
                    });
                }
            })
            .addCase(deleteSubscriber.fulfilled, (state, action) => {
                const {pageId, subscriberId, isCurrentUser} = action.meta.arg;

                deletePageSubscriber(state, {
                    id: pageId,
                    subscriberId,
                });

                const slug = state.byId[pageId];

                if (slug && isCurrentUser) {
                    setPageSubscription(state, {slug});
                }
            })
            .addCase(createSubscription.fulfilled, (state, action) => {
                const slug = getSlugByIdentifier(state, action.meta.arg.page);

                if (slug) {
                    setPageSubscription(state, {
                        slug,
                        subscription: action.meta.arg.isCluster
                            ? PageSubscriptionType.ToCluster
                            : PageSubscriptionType.ToPage,
                    });
                }
            })

            .addCase(deleteSubscriptions.fulfilled, (state, action) => {
                action.meta.arg.pages.forEach((identifier) => {
                    const slug = getSlugByIdentifier(state, identifier);

                    if (slug) {
                        setPageSubscription(state, {slug});
                    }
                });
            })
            .addCase(getPageAcl.pending, (state, {meta}) => {
                setPageAclLoading(state, {slug: meta.arg.slug, isLoading: true});
            })
            .addCase(getPageAcl.fulfilled, (state, {meta, payload}) => {
                const {slug} = meta.arg;

                setPageAclLoading(state, {slug, isLoading: false});
                setPageAcl(state, {slug, acl: payload.acl, access: payload.access});
            })
            .addCase(getPageAcl.rejected, (state, {meta}) => {
                setPageAclLoading(state, {slug: meta.arg.slug, isLoading: false});
            })
            .addCase(updatePageSettings.pending, (state, action) => {
                const {redirect, pageId} = action.meta.arg;

                redirect && setPageRedirectLoading(state, {id: pageId, isLoading: true});
            })
            .addCase(updatePageSettings.rejected, (state, action) => {
                const {pageId} = action.meta.arg;

                setPageRedirectLoading(state, {id: pageId, isLoading: false});
            })
            .addCase(updatePageSettings.fulfilled, (state, action) => {
                const {redirect, forceRedirect} = action.meta.arg;
                const {acl, access, slug, authors, attributes, user_permissions, id} =
                    action.payload;
                setPageAcl(state, {slug, acl, access});
                setPageAuthors(state, {authors, slug});
                setPageAttributes(state, {slug, attributes});
                setPagePermissions(state, {slug, permissions: user_permissions});

                if (redirect) {
                    setPageRedirect(state, {id, redirect});

                    if (redirect.page) {
                        state.redirectTo = getPageRedirectUrl(redirect.page.slug, forceRedirect);
                    }
                }
                setPageRedirectLoading(state, {id, isLoading: false});
            })
            .addCase(updatePageActuality.fulfilled, (state, action) => {
                const {actuality, attributes, slug} = action.payload;
                setPageActuality(state, {slug, actuality});
                setPageAttributes(state, {slug, attributes});
            })
            .addCase(updatePageType.pending, (state) => {
                state.isUpdating = true;
            })
            .addCase(updatePageType.rejected, (state) => {
                state.isUpdating = false;
            })
            .addCase(updatePageType.fulfilled, (state, action) => {
                const {pageId} = action.meta.arg;

                const slug = state.byId[pageId];

                if (!isString(slug)) {
                    return;
                }

                const {
                    page_type: type,
                    last_revision_id: lastRevisionId,
                    active_revision: activeRevision,
                    content,
                } = action.payload;

                setPageContent(state, {
                    slug,
                    pageContent: {content, type} as DocTypeContent,
                });

                const entity = state.entities[slug];

                if (!entity) {
                    return;
                }

                entity.pageType = type;
                entity.lastRevisionId = lastRevisionId;
                entity.revisionsData = undefined;

                entity.revision = activeRevision ? activeRevision : undefined;
                entity.html = undefined;
                entity.translatedHtml = undefined;
                entity.previewYfm = false;

                state.isUpdating = false;
            })
            .addCase(requestAuthorRole.fulfilled, (state, action) => {
                const page = action.payload;

                if (!page) {
                    return;
                }
                const {
                    slug,
                    page_type: pageType,
                    user_permissions: userPermissions,
                    authors,
                    ...rest
                } = page;

                setPageDetails(state, {pageType, slug, userPermissions, ...rest});
                setPageAuthors(state, {authors, slug});
            })
            .addCase(updatePageBackground.pending, (state, action) => {
                const {pageId, background} = action.meta.arg;

                setPageBackground(state, {id: pageId, background});
            })
            .addCase(updatePageBackground.rejected, (state, action) => {
                const {pageId, prevBackground} = action.meta.arg;

                setPageBackground(state, {id: pageId, background: prevBackground});
            })
            .addCase(updatePageContent.pending, (state) => {
                state.isUpdating = true;
            })
            .addCase(updatePageContent.rejected, (state) => {
                state.isUpdating = false;
            })
            .addCase(updatePageContent.fulfilled, (state, action) => {
                const {id, content, page_type: type} = action.payload;

                const slug = state.byId[id];

                if (!isString(slug)) return;

                setPageContent(state, {slug, pageContent: {content, type} as DocTypeContent});

                const entity = state.entities[slug];

                if (!entity) return;

                const lastRevisionId = action.payload.last_revision_id;

                entity.lastRevisionId = lastRevisionId;
                entity.revisionsData = undefined;

                const activeRevision = action.payload.active_revision;
                entity.revision = activeRevision ? activeRevision : undefined;
                entity.html = undefined;
                entity.translatedHtml = undefined;
                state.isUpdating = false;
            })
            .addCase(deletePage.fulfilled, (state, action) => {
                const {pageId, redirectSlug} = action.meta.arg;
                const slug = state.byId[pageId];

                if (redirectSlug) {
                    state.current = redirectSlug;
                }

                if (!isString(slug)) return;

                const entity = state.entities[slug];

                if (!entity) return;

                delete state.entities[slug];

                state.stubEntities[slug] = {
                    title: getDeletedPageTitle(slug),
                    slug,
                    breadcrumbs: entity.breadcrumbs,
                    clusterSlug: entity.clusterSlug,
                    userPermissions: entity.userPermissions,
                };
            })
            .addCase(updatePageRedirect.pending, (state, action) => {
                const {pageId} = action.meta.arg;

                setPageRedirectLoading(state, {id: pageId, isLoading: true});
            })
            .addCase(updatePageRedirect.rejected, (state, action) => {
                const {pageId} = action.meta.arg;

                setPageRedirectLoading(state, {id: pageId, isLoading: false});
            })
            .addCase(updatePageRedirect.fulfilled, (state, action) => {
                const {pageId, redirect, forceRedirect} = action.meta.arg;

                setPageRedirect(state, {id: pageId, redirect});

                if (redirect && redirect.page) {
                    state.redirectTo = getPageRedirectUrl(redirect.page.slug, forceRedirect);
                }

                setPageRedirectLoading(state, {id: pageId, isLoading: false});
            })
            .addCase(translatePage.fulfilled, (state, action) => {
                const {id} = action.meta.arg;
                const slug = state.byId[id];

                if (!slug) {
                    return;
                }

                const entity = state.entities[slug];

                if (!entity) {
                    return;
                }

                const {title, html} = action.payload;

                setPageContent(state, {
                    slug,
                    pageContent: {
                        type: PageType.Page,
                        translatedHtml: html,
                    },
                });

                entity.translatedTitle = title;
            })
            .addCase(getRevisions.fulfilled, (state, action) => {
                const {payload} = action;
                const {pageId, cursor} = action.meta.arg;

                setPageRevisions(state, {
                    id: pageId,
                    revisions: payload.results,
                    firstPage: !cursor,
                    nextCursor: payload.next_cursor || undefined,
                });
            })
            .addCase(revisionActions.reset, (state, {payload: {pageId}}) => {
                const slug = state.byId[pageId];

                if (!isString(slug)) {
                    return;
                }

                const entity = state.entities[slug];

                if (!entity) {
                    return;
                }

                entity.revisions = [];
            })
            .addCase(getAttachments.pending, (state, action) => {
                const {pageId} = action.meta.arg;
                const slug = state.byId[pageId];

                if (!isString(slug)) {
                    return;
                }
                setPageAttachmentsLoading(state, {slug, isLoading: true});
                setPageAttachmentsError(state, {slug, isError: false});
            })
            .addCase(getAttachments.rejected, (state, action) => {
                const {pageId} = action.meta.arg;
                const slug = state.byId[pageId];

                if (!isString(slug)) {
                    return;
                }
                setPageAttachmentsLoading(state, {slug, isLoading: false});
                setPageAttachmentsError(state, {slug, isError: true});
            })
            .addCase(getAttachments.fulfilled, (state, action) => {
                const {payload} = action;
                const {pageId, cursor} = action.meta.arg;
                const slug = state.byId[pageId];

                if (isString(slug)) {
                    setPageAttachmentsLoading(state, {slug, isLoading: false});
                }

                setPageAttachments(state, {
                    id: pageId,
                    attachments: payload.results,
                    firstPage: !cursor,
                    nextCursor: payload.next_cursor || undefined,
                });
            })
            .addCase(attachmentsActions.reset, (state, {payload: {pageId}}) => {
                const slug = state.byId[pageId];

                if (!isString(slug)) {
                    return;
                }

                setPageAttachmentsLoading(state, {slug, isLoading: false});
                setPageAttachmentsError(state, {slug, isError: false});

                const entity = state.entities[slug];

                if (!entity) {
                    return;
                }

                entity.attachments = [];
            })
            .addCase(commentsActions.reset, (state, {payload: {pageId}}) => {
                const slug = state.byId[pageId];

                if (!isString(slug)) {
                    return;
                }

                const entity = state.entities[slug];

                if (!entity) {
                    return;
                }

                entity.comments = [];
            })
            .addCase(deleteAttachment.fulfilled, (state, action) => {
                const {attachmentId, pageId} = action.meta.arg;

                deletePageAttachment(state, {id: attachmentId, pageId});
            })
            .addCase(attachFiles.fulfilled, (state, action) => {
                const attachments = action.payload;
                const {pageId} = action.meta.arg;

                attachFilesToPage(state, {pageId, attachments});
            })
            .addCase(finishPagesMove, (state, action) => {
                updatePagesSlugs(state, action.payload.slugsToUpdate);
            })
            .addMatcher(
                isAnyOf(
                    createPageWithDetails.fulfilled,
                    importPageWithDetails.fulfilled,
                    getPageByIdWithDetails.fulfilled,
                ),
                (state, action) => {
                    state.error = undefined;

                    const {
                        id,
                        slug,
                        page_type: pageType,
                        authors,
                        bookmark,
                        cluster,
                        subscription,
                        user_permissions: userPermissions,
                        last_revision_id: lastRevisionId,
                        attributes,
                        content,
                        access,
                        ...rest
                    } = action.payload;

                    const pageDetails = {
                        id,
                        slug,
                        pageType,
                        bookmarkId: bookmark?.id,
                        clusterSlug: cluster.slug,
                        subscription: subscription?.status,
                        userPermissions,
                        lastRevisionId,
                        access: access
                            ? {
                                  type: access.type,
                                  isReadonly: access.is_readonly,
                                  hasExternal: access.has_external,
                                  inheritsFrom: access.inherits_from,
                              }
                            : undefined,
                        ...rest,
                    };

                    delete state.stubEntities[slug];

                    state.toolboxType = 'normal';

                    setPageDetails(state, pageDetails);
                    setPageAuthors(state, {slug, authors});
                    setPageAttributes(state, {slug, attributes});
                    setPageContent(state, {
                        slug,
                        pageContent: {type: pageType, content} as DocTypeContent,
                    });
                },
            )
            .addMatcher(isAnyOf(createPage.fulfilled, importPage.fulfilled), (state, action) => {
                const {pageType, id, slug, title} = action.payload;

                setPageDetails(state, {pageType, id, slug, title});
            }),
});

export {actions, reducer};
